"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, LABEL } from "@/components/tool-kit/styles";
import { SubtitleResults } from "@/components/tools/qualitative-text/subtitle-results";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type ConvertSettings,
  convertSubtitles,
  type TimingMode,
} from "@/lib/tool-kit/qualitative-text/subtitle-convert";
import {
  FRAME_RATES,
  formatSubtitleTime,
  type SubtitleFormat,
} from "@/lib/tool-kit/qualitative-text/subtitles";

/** English strings. A translated content file overrides these through `ui`. */
export const SUBTITLE_CONVERTER_STRINGS = {
  privacy:
    "Runs entirely in your browser. Your subtitles and any file you open are never uploaded.",
  inputLabel: "Subtitles to convert",
  inputPlaceholder: "Paste the contents of an .srt or .vtt file.",
  fileLabel: "Or open a .srt or .vtt file",
  sampleSrt: "Sample: SRT",
  sampleVtt: "Sample: WebVTT",
  clear: "Clear",
  targetLabel: "Convert to",
  targets: { vtt: "WebVTT (.vtt)", srt: "SubRip (.srt)" } as Record<
    SubtitleFormat,
    string
  >,
  modeLabel: "Change the timing",
  modes: {
    none: "Leave the timing alone",
    shift: "Shift every cue by a fixed time",
    stretch: "Stretch by a factor",
    fps: "Convert between frame rates",
    sync: "Match the first and last cue to two known times",
  } as Record<TimingMode, string>,
  shiftLabel: "Seconds to add (negative moves earlier)",
  shiftHint: "Decimals are exact: 2.5 is 2,500 ms, -0.04 is −40 ms.",
  factorLabel: "Multiply every time by",
  factorHint:
    "1.001 makes every cue 0.1% later; 0.95904 slows 25 fps timing to 23.976.",
  fpsFromLabel: "Subtitles are timed for (fps)",
  fpsToLabel: "Video plays at (fps)",
  syncFirstLabel: "First cue should start at",
  syncLastLabel: "Last cue should start at",
  syncHint:
    "Times as 1:02:03.5 or 62.5 seconds. Currently they start at {first} and {last}.",
  optionsHeading: "Output",
  keepIds: "Keep cue identifiers in WebVTT",
  renumber: "Renumber WebVTT cues from 1 (SubRip is always renumbered)",
  keepSettings: "Keep WebVTT cue settings (position, align)",
  stripTags: "Remove all formatting tags",
  voiceToLabel: "Write <v Name> speakers as “Name: ” in SubRip",
  sort: "Sort cues by start time",
  emptyPrompt: "Paste subtitles or open a file to convert them.",
  noCues:
    "No cues were found. A cue needs a timing line such as 00:00:01,000 --> 00:00:03,000.",
  badValue:
    "One of the timing values cannot be used. Check that each is a number or time, and that a factor or a pair of times is above zero.",
  results: "Converted subtitles",
  detected: "Read as {format}.",
  statCues: "Cues",
  statSpan: "First start to last end",
  spanFormat: "{start} – {end}",
  statDropped: "Dropped before zero",
  statClamped: "Moved up to zero",
  checksHeading: "Things to check",
  checkOutOfOrder:
    "one: {count} cue starts before the one above it|other: {count} cues start before the one above them",
  checkOverlap:
    "one: {count} cue starts before the previous one ends|other: {count} cues start before the previous one ends",
  checkEmpty: "one: {count} cue has no text|other: {count} cues have no text",
  checkZero:
    "one: {count} cue ends no later than it starts|other: {count} cues end no later than they start",
  checksClean:
    "No overlaps, ordering problems, empty cues or zero-length cues.",
  previewHeading: "First cues, before and after",
  colNumber: "#",
  colBefore: "Starts (before)",
  colAfter: "Starts (after)",
  colText: "Text",
  outputLabel: "Converted file",
  copy: "Copy",
  copied: "Copied!",
  download: "Download file",
  filenames: { srt: "subtitles.srt", vtt: "subtitles.vtt" } as Record<
    SubtitleFormat,
    string
  >,
  sampleSrtText:
    "1\n00:00:01,000 --> 00:00:03,500\nGood morning, and welcome.\n\n2\n00:00:04,000 --> 00:00:07,250\nToday we look at <i>sleep</i> & memory.\n\n3\n00:00:08,000 --> 00:00:10,000\nFirst, a quick question.",
  sampleVttText:
    "WEBVTT\n\nNOTE Interview 4\n\n1\n00:01.000 --> 00:03.500 align:start\n<v Priya Nair>Good morning, and welcome.</v>\n\n2\n00:04.000 --> 00:07.250\n<v Tom Hale>Thanks for having me.</v>",
} as const;

export default function SubtitleConverterWidget() {
  const strings = useToolUi(SUBTITLE_CONVERTER_STRINGS);
  const [text, setText] = useState("");
  const [target, setTarget] = useState<SubtitleFormat>("vtt");
  const [mode, setMode] = useState<TimingMode>("none");
  const [shift, setShift] = useState("0");
  const [factor, setFactor] = useState("1");
  const [fpsFrom, setFpsFrom] = useState("23.976");
  const [fpsTo, setFpsTo] = useState("25");
  const [syncFirst, setSyncFirst] = useState("");
  const [syncLast, setSyncLast] = useState("");
  const [keepIds, setKeepIds] = useState(true);
  const [renumber, setRenumber] = useState(false);
  const [keepSettings, setKeepSettings] = useState(true);
  const [stripTags, setStripTags] = useState(false);
  const [voiceToLabel, setVoiceToLabel] = useState(true);
  const [sort, setSort] = useState(false);
  const deferred = useDeferredValue(text);

  const settings: ConvertSettings = useMemo(
    () => ({
      target,
      mode,
      shift,
      factor,
      fpsFrom,
      fpsTo,
      syncFirst,
      syncLast,
      sort,
      write: { keepIds, renumber, keepSettings, stripTags, voiceToLabel },
    }),
    [
      target,
      mode,
      shift,
      factor,
      fpsFrom,
      fpsTo,
      syncFirst,
      syncLast,
      sort,
      keepIds,
      renumber,
      keepSettings,
      stripTags,
      voiceToLabel,
    ]
  );
  const result = useMemo(
    () => (deferred.trim() ? convertSubtitles(deferred, settings) : null),
    [deferred, settings]
  );

  async function openFile(file: File | undefined) {
    if (file) {
      setText(await file.text());
    }
  }

  const frameOptions = FRAME_RATES.map((rate) => ({
    value: rate.label,
    label: rate.label,
  }));
  const starts = result?.before.map((cue) => cue.start) ?? [];
  const firstStart = starts.length ? Math.min(...starts) : 0;
  const lastStart = starts.length ? Math.max(...starts) : 0;

  return (
    <>
      <Notice>{strings.privacy}</Notice>
      <TextAreaField
        id="sc-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setText(strings.sampleSrtText);
            setTarget("vtt");
          }}
          type="button"
        >
          {strings.sampleSrt}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setText(strings.sampleVttText);
            setTarget("srt");
          }}
          type="button"
        >
          {strings.sampleVtt}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
        <label className={LABEL} htmlFor="sc-file">
          {strings.fileLabel}
        </label>
        <input
          accept=".srt,.vtt,.txt,text/plain,text/vtt"
          className="text-foreground text-sm"
          id="sc-file"
          onChange={(event) => {
            openFile(event.target.files?.[0]);
            event.target.value = "";
          }}
          type="file"
        />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <PillGroup
          label={strings.targetLabel}
          name="sc-target"
          onChange={setTarget}
          options={(Object.keys(strings.targets) as SubtitleFormat[]).map(
            (value) => ({ value, label: strings.targets[value] })
          )}
          value={target}
        />
        <SelectField
          id="sc-mode"
          label={strings.modeLabel}
          onChange={setMode}
          options={(Object.keys(strings.modes) as TimingMode[]).map(
            (value) => ({ value, label: strings.modes[value] })
          )}
          value={mode}
        />
        {mode === "shift" ? (
          <TextField
            hint={strings.shiftHint}
            id="sc-shift"
            label={strings.shiftLabel}
            numeric
            onChange={setShift}
            value={shift}
          />
        ) : null}
        {mode === "stretch" ? (
          <TextField
            hint={strings.factorHint}
            id="sc-factor"
            label={strings.factorLabel}
            numeric
            onChange={setFactor}
            value={factor}
          />
        ) : null}
        {mode === "fps" ? (
          <>
            <SelectField
              id="sc-fps-from"
              label={strings.fpsFromLabel}
              onChange={setFpsFrom}
              options={frameOptions}
              value={fpsFrom}
            />
            <SelectField
              id="sc-fps-to"
              label={strings.fpsToLabel}
              onChange={setFpsTo}
              options={frameOptions}
              value={fpsTo}
            />
          </>
        ) : null}
        {mode === "sync" ? (
          <>
            <TextField
              hint={t(strings.syncHint, {
                first: formatSubtitleTime(firstStart, "."),
                last: formatSubtitleTime(lastStart, "."),
              })}
              id="sc-sync-first"
              label={strings.syncFirstLabel}
              onChange={setSyncFirst}
              value={syncFirst}
            />
            <TextField
              id="sc-sync-last"
              label={strings.syncLastLabel}
              onChange={setSyncLast}
              value={syncLast}
            />
          </>
        ) : null}
      </div>
      <fieldset className="space-y-2">
        <legend className={LABEL}>{strings.optionsHeading}</legend>
        <Checkbox
          checked={keepIds}
          label={strings.keepIds}
          onChange={setKeepIds}
        />
        <Checkbox
          checked={renumber}
          label={strings.renumber}
          onChange={setRenumber}
        />
        <Checkbox
          checked={keepSettings}
          label={strings.keepSettings}
          onChange={setKeepSettings}
        />
        <Checkbox
          checked={stripTags}
          label={strings.stripTags}
          onChange={setStripTags}
        />
        <Checkbox
          checked={voiceToLabel}
          label={strings.voiceToLabel}
          onChange={setVoiceToLabel}
        />
        <Checkbox checked={sort} label={strings.sort} onChange={setSort} />
      </fieldset>
      {result ? (
        <SubtitleResults result={result} strings={strings} target={target} />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
